import type { Product, CategoryFiltersState } from '~/types/database'

interface ProductsResult {
  products: Product[]
  total: number
}

interface CacheEntry {
  data: ProductsResult
  timestamp: number
}

// Short-lived cache TTL: 2 minutes
const CACHE_TTL = 2 * 60 * 1000

// Client-side only cache (SSR-safe: module-level cache only used on client)
let queryCache: Map<string, CacheEntry> | null = null

const getQueryCache = (): Map<string, CacheEntry> => {
  // Only use module-level cache on client side
  if (import.meta.client) {
    if (!queryCache) {
      queryCache = new Map()
    }
    return queryCache
  }
  // On server, return empty map (no caching across requests)
  return new Map()
}

/**
 * Escape special ILIKE pattern characters to prevent injection
 * Characters: % (wildcard), _ (single char), \ (escape)
 */
const escapeILikePattern = (str: string): string => {
  return str
    .replace(/\\/g, '\\\\') // Escape backslash first
    .replace(/%/g, '\\%')   // Escape percent
    .replace(/_/g, '\\_')   // Escape underscore
}

/**
 * Composable for fetching products by category with dynamic filters
 */
export const useProductsByCategory = () => {
  const supabase = useSupabaseClient()

  /**
   * Generate cache key from query parameters
   */
  const getCacheKey = (
    categoryId: string,
    filters: CategoryFiltersState,
    search: string,
    page: number,
    pageSize: number
  ): string => {
    return JSON.stringify({ categoryId, filters, search, page, pageSize })
  }

  /**
   * Check if cache entry is valid
   */
  const isCacheValid = (entry: CacheEntry): boolean => {
    return Date.now() - entry.timestamp < CACHE_TTL
  }

  /**
   * Fetch products for a category with filters and pagination
   */
  const fetchProducts = async (
    categoryId: string,
    filters: CategoryFiltersState = {},
    search: string = '',
    page: number = 1,
    pageSize: number = 24
  ): Promise<ProductsResult> => {
    // Check cache (client-side only)
    const cache = getQueryCache()
    const cacheKey = getCacheKey(categoryId, filters, search, page, pageSize)
    const cached = cache.get(cacheKey)
    if (cached && isCacheValid(cached)) {
      return cached.data
    }

    try {
      // Build base query - use simple select to avoid PostgREST schema cache issues
      let query = supabase
        .from('products')
        .select('*', { count: 'exact' })
        .eq('category_id', categoryId)
        .eq('is_active', true)

      // Apply search with escaped special characters
      const trimmedSearch = search?.trim()
      if (trimmedSearch) {
        const escapedSearch = escapeILikePattern(trimmedSearch)
        query = query.or(
          `name.ilike.%${escapedSearch}%,stock_code.ilike.%${escapedSearch}%,brand.ilike.%${escapedSearch}%,description.ilike.%${escapedSearch}%`
        )
      }

      // Apply dynamic filters
      for (const [key, value] of Object.entries(filters)) {
        if (value === null || value === undefined || value === '' || value === 'all') {
          continue
        }

        // Standard column filters
        const columnFilters = ['subcategory', 'brand', 'product_line_id', 'component_type']

        if (columnFilters.includes(key)) {
          if (Array.isArray(value)) {
            query = query.in(key, value as string[])
          } else {
            query = query.eq(key, value as string)
          }
        } else {
          // JSONB attribute filters (category_attributes column)
          if (Array.isArray(value)) {
            if (value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
              // Range filter [min, max]
              query = query
                .gte(`category_attributes->>${key}`, value[0])
                .lte(`category_attributes->>${key}`, value[1])
            } else {
              // Multi-select filter - check if attribute contains any of the values
              // Using OR for multiple values in JSONB
              const orConditions = (value as string[])
                .map(v => `category_attributes->>${key}.eq.${v}`)
                .join(',')
              query = query.or(orConditions)
            }
          } else if (typeof value === 'boolean') {
            query = query.eq(`category_attributes->>${key}`, value.toString())
          } else {
            query = query.eq(`category_attributes->>${key}`, value as string)
          }
        }
      }

      // Pagination
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      // Execute query with ordering and pagination
      const { data, count, error } = await query
        .order('name')
        .range(from, to)

      if (error) {
        throw error
      }

      const result: ProductsResult = {
        products: (data || []) as Product[],
        total: count || 0
      }

      // Cache result (client-side only)
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      })

      return result
    } catch (e) {
      console.error('Error fetching products by category:', e)

      // Return stale cache if available
      if (cached) {
        return cached.data
      }

      return { products: [], total: 0 }
    }
  }

  /**
   * Clear the query cache
   */
  const clearCache = () => {
    getQueryCache().clear()
  }

  /**
   * Clear cache for a specific category
   */
  const clearCategoryCache = (categoryId: string) => {
    const cache = getQueryCache()
    for (const key of cache.keys()) {
      if (key.includes(categoryId)) {
        cache.delete(key)
      }
    }
  }

  return {
    fetchProducts,
    clearCache,
    clearCategoryCache
  }
}
